import React, { useEffect } from "react";
import {
  TextField,
  Title,
  HelperText,
  ApiLoadingSpinner,
  ApiUpdatingSpinner,
  SelectField,
  SelectFieldOption,
} from "@funcional/react-ui";
import { useFieldArray, useFormContext } from "react-hook-form";
import { IProductbyPrograms } from "../contracts";
import { Grid } from "@material-ui/core";
import { DiscountRangesForm } from "./DiscountRangesForm";
import { v4 as uuid } from "uuid";
import { AutocompleteField } from "./AutocompleteField";
import { useQueryClient } from "react-query";
import { CursorPaginated, Edge } from "@types";
import { IRefundRule } from "@refund/query-fixed-discount-rules/contracts";

const containerSpacing = 3;

interface IProductCustomFields {
  label: string | React.ReactNode;
  disabled: boolean;
  errorMessage: string;
  required: boolean;
  disableClearable?: boolean;
}

export function ProductAndDiscountForm({
  programCode,
  defaults,
  editMode,
}: {
  programCode?: string;
  defaults?: IRefundRule;
  editMode?: boolean;
}) {
  const {
    register,
    control,
    formState: { errors },
    getValues,
    setValue,
    setError,
    watch,
  } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "ranges",
  });
  const addOption = () => {
    append([
      {
        id: uuid(),
        discount: "",
        discountRefund: "",
      } as never,
    ]);
  };
  const deleteOption = (row: any) => {
    if (window.confirm("Tem certeza de que deseja excluir esta faixa?")) {
      remove(row.index);
    }
  };
  const productData = useQueryClient().getQueryState(
    `refund_program_products:${programCode}`
  ) as any;
  let products: IProductbyPrograms[] = [],
    data: CursorPaginated<IProductbyPrograms>,
    status,
    isFetching,
    productOptionsFieldValue,
    productOnChange;

  if (!editMode) {
    data = productData.data;
    status = productData.status;
    isFetching = productData.isFetching;

    products = data?.edges?.map((value: Edge<IProductbyPrograms>) => value.node);

    productOnChange = () => {
      const selectedProduct = getValues("product");
      const selectedProductId = products
        ?.filter((product) => selectedProduct.includes(`${product.name} (${product.description})`))
        .map((product) => product.id);
      setValue("productId", selectedProductId);
    };
  }

  productOptionsFieldValue =
    products?.map((product) => {
      return {
        value: `${product.name} (${product.description})`,
        label: `${product.name} (${product.description})`,
      };
    }) || [];

  const productDefaultFieldValue = () => {
    const product = getValues("product");
    console.log('edit product', product);
    return product ? {
      value: `${product.name} (${product.description})`,
      label: `${product.name} (${product.description})`,
    } : [];
  };

  const getProductFields = (): IProductCustomFields => {
    let label: string | React.ReactNode;
    let required = false;
    let disabled = true;
    let errorMessage = "";

    if (!editMode) {
      if (Object.keys(productDefaultFieldValue).length > 0) {
        label = "Selecione o produto";
        required = true;
        disabled = false;
      } else {
        if (status === "loading" || status === "idle") {
          label = (
            <div className="relative text-clip -top-3">
              <ApiLoadingSpinner />
            </div>
          );
        } else if (status === "error") {
          label = "Ocorreu um erro ao buscar os produtos.";
          errorMessage =
            "Ocorreu um erro ao buscar os dados dos produtos, tente novamente mais tarde. Caso o problema persista, entre em contato com o administrador do sistema.";
        } else if (isFetching) {
          label = (
            <div className="relative text-clip -top-3">
              <ApiUpdatingSpinner />
            </div>
          );
        } else if (productOptionsFieldValue.length === 0) {
          label = "Programa sem produtos cadastrados";
          errorMessage =
            "Não foram encontrados produtos cadastrados para esse programa, portanto não será possível cadastrar uma nova regra.";
        } else {
          label = "Selecione o produto";
          required = true;
          disabled = false;
        }
      }
    }
    return {
      label: editMode ? "Produto" : label,
      errorMessage,
      disabled: editMode ? true : disabled,
      required,
    };
  };

  const getEffetiveUntilErrorMessage = (errorMessage) => {
    const errorMessageField = {
      errorMessage: errorMessage,
    };
    if (editMode) {
      if (
        defaults &&
        new Date() > new Date(defaults.endDate) &&
        endDateVal === defaults.endDate
      ) {
        errorMessageField.errorMessage =
          "a data e hora final da vigência não pode ser inferior a data e hora atual";
      }
    }
    return errorMessageField;
  };

  const getFormulaAttributesStateValue = () => {
    if (getValues("formulaAttributes")) {
      if (getValues("formulaAttributes") instanceof Array) {
        return JSON.stringify(getValues("formulaAttributes"));
      } else {
        return getValues("formulaAttributes");
      }
    }
    return "";
  };

  const [startDateVal, setstartDate] = React.useState(
    getValues('startDate') ? getValues('startDate') : '')
  const [endDateVal, setendDate] = React.useState(
    getValues('endDate') ? getValues('endDate') : '')
  const [formulaAttributesVal, setFormulaAttributes] = React.useState(getFormulaAttributesStateValue())

  const watchValue = watch();
  useEffect(() => {
    setFormulaAttributes(getFormulaAttributesStateValue());
  }, [watchValue?.formulaAttributes]);

  return (
    <Grid container spacing={containerSpacing} className="max-w-xl">
      <Grid item xs={6}>
        <TextField
          required
          InputProps={{ inputProps: { max: "9999-12-31T23:59:59" } }}
          control={control}
          onChange={(event) => {
            setstartDate(event.target.value);
            setValue("startDate", event.target.value);
          }}
          type="datetime-local"
          id="startDate"
          label="Início da vigência"
          defaultValue={
            defaults?.startDate ? defaults.startDate : undefined
          }
          errorMessage={errors?.startDate?.message}
        />
      </Grid>

      <Grid item xs={6}>
        <TextField
          required
          InputProps={{
            inputProps: {
              min: new Date().toISOString().slice(0, 16),
              max: "9999-12-31T23:59:59",
            },
          }}
          control={control}
          onChange={(event) => {
            setendDate(event.target.value);
            setValue("endDate", event.target.value);
          }}
          type="datetime-local"
          id="endDate"
          label="Fim da vigência"
          defaultValue={
            defaults?.endDate ? defaults.endDate : undefined
          }
          {...getEffetiveUntilErrorMessage(errors?.endDate?.message)}
        />
      </Grid>

      <Grid item xs={12}>
        <AutocompleteField
          multiple={false}
          disableCloseOnSelect={false}
          freeSolo
          helperText="Este é o produto que receberá o desconto determinado abaixo"
          name="product"
          control={control}
          options={productOptionsFieldValue as any}
          selectBoxOnChange={productOnChange}
          {...getProductFields()}
        />
      </Grid>

      <Grid item xs={12}>
        <SelectField
          required={true}
          errorMessage={errors?.formulaAttributes?.message}
          name="formulaAttributes"
          value={formulaAttributesVal}
          onChange={(event) => {
            setFormulaAttributes(event.target.value);
            setValue("formulaAttributes", event.target.value);
          }}
          control={control}
          defaultValue={formulaAttributesVal}
          id="formulaAttributes"
          label="Fórmula do cálculo de ressarcimento"
          helperText="Selecione abaixo qual o tipo de fórmula a ser considerada no momento do cálculo de ressarcimento."
        >
          <SelectFieldOption value={undefined}>-</SelectFieldOption>
          <SelectFieldOption
            value={JSON.stringify(["INDUSTRY_PRICE", "MULTIPLICATION", "DISCOUNT", "MULTIPLICATION", "SALE_QUANTITY"])}
          >
            Preço da Indústria (Preço do produto na Indústria x Desconto x
            Quantidade vendida)
          </SelectFieldOption>
          <SelectFieldOption
            value={JSON.stringify([
              "UNIT_PRICE",
              "MULTIPLICATION",
              "DISCOUNT",
              "MULTIPLICATION",
              "SALE_QUANTITY",
            ])}
          >
            Preço de Venda (Preço do produto na venda x Desconto x Quantidade
            vendida)
          </SelectFieldOption>
        </SelectField>
      </Grid>

      <Grid item xs={12}>
        <Title level={3}>Faixas de desconto</Title>

        {errors?.ranges?.message ? (
          <HelperText errorMessage={errors?.ranges?.message} />
        ) : null}

        {fields ? (
          <DiscountRangesForm
            register={register as any}
            data={fields as any}
            addOption={addOption}
            deleteOption={deleteOption}
            errors={errors}
          />
        ) : null}
      </Grid>
    </Grid>
  );
}
